import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import LazyImage from "@/my_components/Common/LazyImage";

import { getSearchSuggestions, logSearch } from "@/services/searchService";
import { getTrendingCollection, getLearnersFavouriteCollection } from "@/services/courseCollectionService";
import { ROUTE } from "@/route/app_routes.js";
import { CONFIG } from "@/app_config.js";

const DEBOUNCE_MS = 300;

// Bolds the part of `label` that matches `query`, the way Coursera bolds the matched
// substring inside each suggestion row.
const HighlightMatch = ({ label, query }) => {
  const q = query.trim();
  if (!q) return <>{label}</>;

  const index = label.toLowerCase().indexOf(q.toLowerCase());
  if (index === -1) return <>{label}</>;

  return (
    <>
      {label.slice(0, index)}
      <strong>{label.slice(index, index + q.length)}</strong>
      {label.slice(index + q.length)}
    </>
  );
};

const FAVORITES_PER_PAGE = 3;

const HeaderSearchBar = () => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [trending, setTrending] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favPage, setFavPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [emptyStateLoaded, setEmptyStateLoaded] = useState(false);

  const favPageCount = Math.max(1, Math.ceil(favorites.length / FAVORITES_PER_PAGE));
  const visibleFavorites = favorites.slice(favPage * FAVORITES_PER_PAGE, favPage * FAVORITES_PER_PAGE + FAVORITES_PER_PAGE);

  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const hasQuery = query.trim().length > 0;

  // Trending searches + learner favorites only need to load once (on first focus), not on
  // every keystroke -- they back the empty/focused state shown before the visitor types.
  const loadEmptyState = () => {
    if (emptyStateLoaded) return;
    setEmptyStateLoaded(true);
    getTrendingCollection()
      .then((res) => setTrending(res.data || []))
      .catch(() => {});
    getLearnersFavouriteCollection()
      .then((res) => setFavorites(res.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (!hasQuery) {
      setMatches([]);
      setSuggestions([]);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      getSearchSuggestions(query.trim())
        .then((res) => {
          setMatches(res.data?.matches || []);
          setSuggestions(res.data?.suggestions || []);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const commitSearch = (term) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    logSearch(trimmed);
    setOpen(false);
    window.location.href = `${ROUTE.course}?search=${encodeURIComponent(trimmed)}`;
  };

  return (
    <div className="rbt-nav-search" ref={wrapperRef}>
      <form
        className="rbt-nav-search__form"
        onSubmit={(e) => {
          e.preventDefault();
          commitSearch(query);
        }}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="What do you want to learn?"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            setOpen(true);
            loadEmptyState();
          }}
        />
        {query && (
          <button type="button" className="rbt-nav-search__clear" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label="Clear search">
            <i className="feather-x"></i>
          </button>
        )}
        <button type="submit" className="rbt-nav-search__submit" aria-label="Search">
          <i className="feather-search"></i>
        </button>
      </form>

      {open && (
        <div className="rbt-nav-search__panel">
          {!hasQuery ? (
            <>
              {trending.length > 0 && (
                <div className="rbt-nav-search__section">
                  <h6 className="rbt-nav-search__section-title">Trending on {CONFIG.APP_NAME}</h6>
                  <div className="rbt-nav-search__trending-strip">
                    {trending.map((course) => (
                      <Link
                        key={course.id}
                        href={`${ROUTE.course_details}${course.id}`}
                        className="rbt-nav-search__trending-chip"
                        onClick={() => setOpen(false)}
                      >
                        <span className="rbt-nav-search__trending-thumb">
                          <LazyImage src={course.course_image_small} alt={course.title} fill sizes="72px" style={{ objectFit: "cover" }} />
                        </span>
                        <span className="rbt-nav-search__trending-title">{course.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {favorites.length > 0 && (
                <div className="rbt-nav-search__section">
                  <div className="rbt-nav-search__section-head">
                    <h6 className="rbt-nav-search__section-title">Start with these learner favorites</h6>
                    {favPageCount > 1 && (
                      <div className="rbt-nav-search__fav-nav">
                        <button
                          type="button"
                          className="rbt-nav-search__fav-arrow"
                          aria-label="Previous favorites"
                          onClick={() => setFavPage((p) => (p - 1 + favPageCount) % favPageCount)}
                        >
                          <i className="feather-chevron-left"></i>
                        </button>
                        <span className="rbt-nav-search__fav-page">{favPage + 1}/{favPageCount}</span>
                        <button
                          type="button"
                          className="rbt-nav-search__fav-arrow"
                          aria-label="Next favorites"
                          onClick={() => setFavPage((p) => (p + 1) % favPageCount)}
                        >
                          <i className="feather-chevron-right"></i>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="rbt-nav-search__favorites">
                    {visibleFavorites.map((course) => (
                      <Link
                        key={course.id}
                        href={`${ROUTE.course_details}${course.id}`}
                        className="rbt-nav-search__favorite-card"
                        onClick={() => setOpen(false)}
                      >
                        <div className="rbt-nav-search__favorite-thumb">
                          <LazyImage src={course.course_image_small} alt={course.title} fill sizes="200px" style={{ objectFit: "cover" }} />
                        </div>
                        <span className="rbt-nav-search__favorite-title">{course.title}</span>
                        <span className="rbt-nav-search__favorite-meta">
                          {course.skill_level ? `${course.skill_level.charAt(0).toUpperCase()}${course.skill_level.slice(1)}` : "All levels"}
                          {course.category ? ` · ${course.category}` : ""}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {trending.length === 0 && favorites.length === 0 && (
                <p className="rbt-nav-search__status">Start typing to search our free courses.</p>
              )}
            </>
          ) : loading ? (
            <p className="rbt-nav-search__status">Searching&hellip;</p>
          ) : matches.length === 0 && suggestions.length === 0 ? (
            <p className="rbt-nav-search__status">No courses found for &quot;{query.trim()}&quot;.</p>
          ) : (
            <>
              {matches.map((course) => (
                <Link
                  key={course.id}
                  href={`${ROUTE.course_details}${course.id}`}
                  className="rbt-nav-search__match"
                  onClick={() => { logSearch(query.trim()); setOpen(false); }}
                >
                  <div className="rbt-nav-search__match-thumb">
                    <LazyImage src={course.course_image_small} alt={course.title} fill sizes="88px" style={{ objectFit: "cover" }} />
                  </div>
                  <div>
                    <span className="rbt-nav-search__favorite-badge">{CONFIG.NICK_NAME}</span>
                    <span className="rbt-nav-search__match-title">{course.title}</span>
                    <span className="rbt-nav-search__match-meta">
                      {course.instructor ? `${course.instructor} · ` : ""}
                      {course.skill_level ? `${course.skill_level.charAt(0).toUpperCase()}${course.skill_level.slice(1)}` : "All levels"}
                    </span>
                  </div>
                </Link>
              ))}

              {suggestions.length > 0 && (
                <div className="rbt-nav-search__suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={`${s.type}-${s.label}`}
                      type="button"
                      className="rbt-nav-search__suggestion-row"
                      onClick={() => commitSearch(s.label)}
                    >
                      <i className="feather-search"></i>
                      <span><HighlightMatch label={s.label} query={query} /></span>
                    </button>
                  ))}
                </div>
              )}

              <button type="button" className="rbt-nav-search__view-all" onClick={() => commitSearch(query)}>
                See all results for &quot;{query.trim()}&quot;
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearchBar;
